import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect } from 'expo-router';
import fastDeepEqual from 'fast-deep-equal';

import { useLoyaltyUserLazyQuery } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';

import type { IUseLoyaltyUserState } from './types';
import { useIsLoyaltyEnabled } from './use-is-loyalty-enabled';
import { useLoyaltyUserId } from './use-loyalty-user';

/**
  Get loyalty user state (Rewards Points etc.) fresh or from redux. will always populate redux with latest.
  ⚠️ Just need loyaltyId? use useLoyaltyUserId hook ⚠️
 */
export const useLoyaltyUserState = ({
  refreshOnMountOrFocus,
  onSuccess,
}: {
  /** ensure loyalty info isn't stale */
  refreshOnMountOrFocus: boolean;
  onSuccess?: Function;
}): IUseLoyaltyUserState => {
  const dispatch = useAppDispatch();
  const { loyaltyUser: cacheLoyaltyUser, loading: cacheIsLoading } = useAppSelector(
    selectors.loyalty.selectLoyaltyUser
  );
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const loyaltyEnabled = useIsLoyaltyEnabled();

  // TODO: move this to offer's context
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));

  const [
    fetchLoyaltyUser,
    { data, loading: localLoading, error, called },
  ] = useLoyaltyUserLazyQuery({
    variables: { loyaltyId },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  // Mimic useEvent for callback passed as props
  // Refer to: https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Load if loyalty is enabled and a loyaltyId exists. If refreshOnMountOrFocus is true, always fetch fresh data; otherwise, fetch only if there is no cached loyalty user and the query hasn't been called yet.
  const shouldLoad =
    loyaltyEnabled && loyaltyId && (refreshOnMountOrFocus || (!cacheLoyaltyUser && !called));

  const refresh = useCallback(async () => {
    dispatch(actions.loyalty.setLoyaltyLoading(true));
    try {
      const resp = await fetchLoyaltyUser();
      const loyaltyUser = resp.data?.loyaltyUserV2;
      if (!fastDeepEqual(loyaltyUser, cacheLoyaltyUser)) {
        dispatch(actions.loyalty.setUser(loyaltyUser));
      }

      const offerRedemptionAvailableAfter =
        loyaltyUser?.offerRedemptionAvailability?.availableAfter;
      if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
        dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
      }

      updateLaunchDarklyUser(
        {
          loyaltyId: loyaltyUser?.id,
        },
        'loyalty user id change'
      );

      if (onSuccessRef.current) {
        onSuccessRef.current();
      }
      return resp;
    } finally {
      dispatch(actions.loyalty.setLoyaltyLoading(false));
    }
  }, [dispatch, fetchLoyaltyUser, offersCooldownEnabled, cacheLoyaltyUser]);

  const loadLoyaltyUser = useCallback(() => {
    if (shouldLoad) {
      refresh();
    }
  }, [shouldLoad, refresh]);

  // for on mount
  useEffect(() => {
    loadLoyaltyUser();
  }, [loadLoyaltyUser]);

  // for when expo router refocuses the already loaded screen
  useFocusEffect(loadLoyaltyUser);

  const loyaltyUser = data?.loyaltyUserV2 ?? cacheLoyaltyUser;

  const loading = localLoading || cacheIsLoading;

  return {
    loyaltyUser,
    error,
    refetch: refresh,
    loading,
  };
};
